/**
 * WidgetParamsService — fetches and applies widget-params from API
 * Handles CSS variables, i18n overrides, display flags, and custom CSS loading
 */

import { ENDPOINTS } from '../api/endpoints.js';

export default class WidgetParamsService {
  /**
   * @param {import('../api/ApiClient.js').default} apiClient
   * @param {import('../core/StyleManager.js').default} styleManager
   * @param {import('../i18n/I18n.js').default} i18n
   */
  constructor(apiClient, styleManager, i18n) {
    this._apiClient = apiClient;
    this._styleManager = styleManager;
    this._i18n = i18n;

    this._params = null;
  }

  /**
   * Fetch widget params from API and apply them
   * @param {HTMLElement} rootEl - Widget root element (for CSS vars)
   * @returns {Promise<Object|null>} The custom_params object or null
   */
  async fetchAndApply(rootEl) {
    try {
      const result = await this._apiClient.get(ENDPOINTS.GET_WIDGET_PARAMS);
      const data = result.data || result;
      this._params = data.custom_params || {};

      // 1. Apply CSS custom properties
      this._styleManager.applyWidgetParams(this._params, rootEl);

      // 2. Apply i18n overrides
      this._applyI18nOverrides(this._params.i18n_overrides);

      // 3. Load custom CSS if specified
      if (this._params.custom_css_url) {
        await this._styleManager.loadCustomCss(this._params.custom_css_url);
      }

      return this._params;
    } catch (err) {
      console.warn('[GarderoboSearch] Failed to fetch widget params:', err);
      return null;
    }
  }

  /**
   * Get the fetched params
   * @returns {Object|null}
   */
  getParams() {
    return this._params;
  }

  /**
   * Check if a display feature is enabled
   * @param {string} flag - e.g. 'show_filters', 'show_photo_search'
   * @param {boolean} [defaultValue=true] - Default if flag not set
   * @returns {boolean}
   */
  isEnabled(flag, defaultValue = true) {
    if (!this._params || this._params[flag] === undefined) {
      return defaultValue;
    }
    return !!this._params[flag];
  }

  /** @private Apply i18n string overrides from widget params */
  _applyI18nOverrides(overrides) {
    if (!overrides || typeof overrides !== 'object') return;

    // Overrides are flat dot-notation keys: { "search.placeholder": "Find..." }
    // We need to rebuild the I18n instance with these overrides
    // Since I18n constructor accepts overrides, we patch the internal dict
    for (const [key, value] of Object.entries(overrides)) {
      this._setNestedValue(this._i18n, key, value);
    }
  }

  /** @private Set a nested value on the i18n dict via dot-notation key */
  _setNestedValue(i18n, key, value) {
    // Access the internal dictionary
    const dict = i18n._dict;
    if (!dict) return;

    const parts = key.split('.');
    let current = dict;

    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]] || typeof current[parts[i]] !== 'object') {
        current[parts[i]] = {};
      }
      current = current[parts[i]];
    }

    current[parts[parts.length - 1]] = value;
  }
}
